// analyzerCore.js — moteur d'analyse de wallet de l'onglet "Analyseur".
// Logique pure + appels réseau, sans React (testable seul). Copie corrigée de
// useWalletAnalysis.js (laissé intact) avec :
//   - filtre dust : positions dont le coût < 0,01 SOL ignorées
//   - plafond ROI : ±10 000 % par position
//   - position "fermée" seulement si ≥ 90 % des tokens achetés ont été revendus
//   - base de coût proportionnelle (mêmes règles que scan-bot.js)
//   - aucune persistance : pas de cache d'analyse (chaque appel est frais)

const SOL_DEC = 1_000_000_000;
const HELIUS = 'https://api.helius.xyz/v0';
const HELIUS_RPC = 'https://mainnet.helius-rpc.com/';

export const BOT_THRESHOLD_TPD = 30;
export const MIN_POSITION_COST_SOL = 0.01;
export const MAX_POSITION_ROI_PCT = 10000;
export const MIN_SOLD_FRACTION = 0.9;
const META_MINTS_LIMIT = 30;

const capRoi = (r) => Math.max(-MAX_POSITION_ROI_PCT, Math.min(MAX_POSITION_ROI_PCT, r));

// Métadonnées de tokens (symbole/logo/date de paire) : mémoire du module uniquement,
// perdue au rechargement de la page. Ce n'est pas un cache d'analyse.
const tokenCache = {};

export const isValidSolanaAddress = (a) => /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test((a || '').trim());

// ─── Réseau ───────────────────────────────────────────────────────────────

async function fetchT(url, { signal, timeoutMs = 8000, ...opts } = {}) {
  const ctrl = new AbortController();
  const onAbort = () => ctrl.abort();
  if (signal) { if (signal.aborted) ctrl.abort(); else signal.addEventListener('abort', onAbort, { once: true }); }
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try { return await fetch(url, { ...opts, signal: ctrl.signal }); }
  finally { clearTimeout(t); if (signal) signal.removeEventListener('abort', onAbort); }
}

function heliusError(status) {
  if (status === 429) return new Error('Limite de requêtes API atteinte (429). Attendez quelques minutes et réessayez.');
  if (status === 401 || status === 403) return new Error(`Clé API Helius invalide (${status}). Vérifie ta clé sur helius.dev`);
  return new Error(`Erreur API Helius (${status})`);
}

async function getEnhancedTxnsDeep(addr, key, signal, target = 500, pageSize = 100, maxPages = 5) {
  let all = [], before = null;
  for (let page = 0; page < maxPages; page++) {
    let url = `${HELIUS}/addresses/${addr}/transactions?api-key=${key}&limit=${pageSize}`;
    if (before) url += `&before=${before}`;
    const r = await fetchT(url, { signal });
    if (!r.ok) throw heliusError(r.status);
    const chunk = await r.json();
    if (!Array.isArray(chunk) || chunk.length === 0) break;
    all = all.concat(chunk);
    if (all.length >= target) break;
    const last = chunk[chunk.length - 1];
    if (!last?.signature) break;
    before = last.signature;
    await new Promise(res => setTimeout(res, 200));
  }
  return all;
}

async function getSolPrice(signal) {
  try {
    const r = await fetchT('https://api.binance.com/api/v3/ticker/price?symbol=SOLUSDT', { signal, timeoutMs: 5000 });
    const j = await r.json();
    return parseFloat(j.price) || 150;
  } catch { return 150; }
}

// 1 crédit Helius. null si indisponible (affiché "Non disponible").
async function getSolBalance(addr, key, signal) {
  try {
    const r = await fetchT(`${HELIUS_RPC}?api-key=${key}`, {
      signal, timeoutMs: 6000, method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jsonrpc: '2.0', id: 1, method: 'getBalance', params: [addr] }),
    });
    if (!r.ok) return null;
    const j = await r.json();
    const lamports = j?.result?.value;
    return typeof lamports === 'number' ? lamports / SOL_DEC : null;
  } catch { return null; }
}

async function getTokenMeta(mint, signal) {
  if (tokenCache[mint]) return tokenCache[mint];
  try {
    const r = await fetchT(`https://api.dexscreener.com/latest/dex/tokens/${mint}`, { signal, timeoutMs: 5000 });
    const j = await r.json();
    const pair = j?.pairs?.[0];
    if (pair?.baseToken) {
      const meta = {
        name: pair.baseToken.name || mint.slice(0, 10), symbol: pair.baseToken.symbol || mint.slice(0, 6),
        logoURI: pair.info?.imageUrl || null,
        pairCreatedAt: pair.pairCreatedAt ? Math.round(pair.pairCreatedAt / 1000) : null,
      };
      tokenCache[mint] = meta; return meta;
    }
  } catch {}
  try {
    const r = await fetchT(`https://tokens.jup.ag/token/${mint}`, { signal, timeoutMs: 5000 });
    if (r.ok) {
      const j = await r.json();
      const meta = { name: j.name || mint.slice(0, 10), symbol: j.symbol || mint.slice(0, 6), logoURI: j.logoURI || null, pairCreatedAt: null };
      tokenCache[mint] = meta; return meta;
    }
  } catch {}
  return { name: mint.slice(0, 10) + '…', symbol: mint.slice(0, 6).toUpperCase(), logoURI: null, pairCreatedAt: null };
}

// ─── Livre de positions ───────────────────────────────────────────────────

export function buildBook(txns, addr) {
  const book = {};
  for (const tx of txns) {
    if (tx.type !== 'SWAP') continue;
    let solIn = 0, solOut = 0;
    for (const nt of tx.nativeTransfers || []) {
      if (nt.toUserAccount === addr) solIn += nt.amount / SOL_DEC;
      if (nt.fromUserAccount === addr) solOut += nt.amount / SOL_DEC;
    }
    const solDelta = solIn - solOut;
    const transfers = (tx.tokenTransfers || []).filter(tt => {
      const amt = parseFloat(tt.tokenAmount ?? 0);
      return amt && (tt.toUserAccount === addr || tt.fromUserAccount === addr);
    });
    const mintsInTx = [...new Set(transfers.map(tt => tt.mint))];
    const solPerMint = mintsInTx.length > 0 ? Math.abs(solDelta) / mintsInTx.length : 0;
    for (const tt of transfers) {
      const amt = parseFloat(tt.tokenAmount ?? 0);
      const mint = tt.mint;
      if (!book[mint]) {
        book[mint] = {
          mint, sym: tt.tokenSymbol || mint.slice(0, 6).toUpperCase(), name: tt.tokenName || mint.slice(0, 10) + '…',
          image: null, buys: [], sells: [], pairCreatedAt: null,
        };
      }
      if (tt.toUserAccount === addr) book[mint].buys.push({ amt, costSol: solPerMint, ts: tx.timestamp });
      else book[mint].sells.push({ amt, revSol: solPerMint, ts: tx.timestamp });
    }
  }
  return book;
}

// Une position par mint. Ignore le dust (< 0,01 SOL). "Fermée" seulement si ≥ 90 % des
// tokens achetés ont été revendus ; sinon "En cours" (ROI et PNL non calculés).
export function buildPositions(book, solPrice) {
  const positions = [];
  let ignoredDust = 0;
  for (const pos of Object.values(book)) {
    if (!pos.buys.length) continue;
    const costSol = pos.buys.reduce((s, b) => s + b.costSol, 0);
    if (costSol < MIN_POSITION_COST_SOL) { ignoredDust++; continue; }
    const revSol = pos.sells.reduce((s, x) => s + x.revSol, 0);
    const tokIn  = pos.buys.reduce((s, b) => s + b.amt, 0);
    const tokOut = pos.sells.reduce((s, x) => s + x.amt, 0);
    const firstBuy = pos.buys.reduce((m, b) => (b.ts < m ? b.ts : m), pos.buys[0].ts);
    const lastSell = pos.sells.length ? pos.sells.reduce((m, x) => (x.ts > m ? x.ts : m), pos.sells[0].ts) : null;
    const closed = pos.sells.length > 0 && tokIn > 0 && tokOut >= tokIn * MIN_SOLD_FRACTION;

    let costRealSol = costSol, revRealSol = revSol, pnlSol = null, roi = null;
    if (closed) {
      // Base de coût proportionnelle : on ne compare que des quantités comparables
      costRealSol = costSol * Math.min(1, tokOut / tokIn);
      revRealSol  = revSol * Math.min(1, tokIn / tokOut);
      pnlSol = revRealSol - costRealSol;
      roi = capRoi((pnlSol / costRealSol) * 100);
    }
    positions.push({
      mint: pos.mint, symbol: pos.sym, name: pos.name, image: pos.image, pairCreatedAt: pos.pairCreatedAt,
      entreeTs: firstBuy, sortieTs: closed ? lastSell : null,
      trades: pos.buys.length + pos.sells.length,
      closed, resultat: closed ? (pnlSol >= 0 ? 'Gagnant' : 'Perdant') : 'En cours',
      investedUsd: costSol * solPrice, revenueUsd: revSol * solPrice,          // montants complets
      costRealSol, revRealSol, pnlSol,                                          // réalisés (fermées)
      achatUsd: costRealSol * solPrice, venteUsd: revRealSol * solPrice,
      pnlUsd: closed ? pnlSol * solPrice : null, roi,
      dureeMin: closed ? Math.max(0, Math.round((lastSell - firstBuy) / 60)) : null,
      soldPct: tokIn > 0 ? Math.round((tokOut / tokIn) * 100) : 0,
    });
  }
  return { positions, ignoredDust };
}

async function enrichPositions(positions, signal) {
  const top = [...positions].sort((a, b) => b.trades - a.trades).slice(0, META_MINTS_LIMIT);
  const metas = await Promise.all(top.map(p => getTokenMeta(p.mint, signal)));
  top.forEach((p, i) => {
    const m = metas[i];
    if (m?.symbol && m.symbol.length > 1) p.symbol = m.symbol;
    if (m?.name && m.name.length > 3) p.name = m.name;
    if (m?.logoURI) p.image = m.logoURI;
    if (m?.pairCreatedAt) p.pairCreatedAt = m.pairCreatedAt;
  });
}

// ─── Agrégats ─────────────────────────────────────────────────────────────

export function summarizePositions(positions, solPrice) {
  const closed = positions.filter(p => p.closed);
  const wins = closed.filter(p => p.pnlSol >= 0);
  const losses = closed.filter(p => p.pnlSol < 0);
  const totPnlSol = closed.reduce((s, p) => s + p.pnlSol, 0);
  const totCostSol = closed.reduce((s, p) => s + p.costRealSol, 0);
  const gainSol = wins.reduce((s, p) => s + p.pnlSol, 0);
  const lossSol = losses.reduce((s, p) => s + Math.abs(p.pnlSol), 0);
  const avgGainUsd = wins.length ? (gainSol * solPrice) / wins.length : 0;
  const avgLossUsd = losses.length ? (lossSol * solPrice) / losses.length : 0;
  const winRate = closed.length ? (wins.length / closed.length) * 100 : 0;

  let bestTrade = null, worstTrade = null;
  for (const p of closed) {
    const t = { symbol: p.symbol, name: p.name, image: p.image, pnlUsd: p.pnlUsd, roi: p.roi };
    if (!bestTrade || p.pnlUsd > bestTrade.pnlUsd) bestTrade = t;
    if (!worstTrade || p.pnlUsd < worstTrade.pnlUsd) worstTrade = t;
  }

  let maxStreak = 0, streak = 0;
  for (const p of [...closed].sort((a, b) => a.sortieTs - b.sortieTs)) {
    if (p.resultat === 'Gagnant') { streak++; if (streak > maxStreak) maxStreak = streak; } else streak = 0;
  }

  const dureeMoyMin = closed.length ? Math.round(closed.reduce((s, p) => s + p.dureeMin, 0) / closed.length) : null;

  const delays = [];
  for (const p of positions) {
    if (p.entreeTs && p.pairCreatedAt && p.entreeTs >= p.pairCreatedAt) {
      const d = Math.round((p.entreeTs - p.pairCreatedAt) / 60);
      if (d >= 0 && d < 43200) delays.push(d);
    }
  }
  const avgEntryDelayMin = delays.length >= 3 ? Math.round(delays.reduce((s, v) => s + v, 0) / delays.length) : null;

  const totalAchatUsd = positions.reduce((s, p) => s + p.investedUsd, 0);
  const totalVenteUsd = positions.reduce((s, p) => s + p.revenueUsd, 0);

  return {
    totalTrades: closed.length, wins: wins.length, losses: losses.length, openCount: positions.length - closed.length,
    winRate, overallRoi: totCostSol > 0 ? capRoi((totPnlSol / totCostSol) * 100) : null,
    pnlSol: totPnlSol, pnlUsd: totPnlSol * solPrice, avgGainUsd, avgLossUsd,
    rrRatio: avgLossUsd > 0 ? avgGainUsd / avgLossUsd : null,
    expectedValue: avgGainUsd > 0 && avgLossUsd > 0 && winRate > 0
      ? (winRate / 100) * avgGainUsd - ((100 - winRate) / 100) * avgLossUsd : null,
    maxStreak, bestTrade, worstTrade, dureeMoyMin, avgEntryDelayMin,
    totalAchatUsd, totalVenteUsd, volumeUsd: totalAchatUsd + totalVenteUsd,
  };
}

export function calcPnlCalendar(positions, month, year) {
  const map = {};
  for (const p of positions) {
    if (!p.closed || !p.sortieTs) continue;
    const d = new Date(p.sortieTs * 1000);
    if (d.getMonth() !== month || d.getFullYear() !== year) continue;
    map[d.getDate()] = (map[d.getDate()] || 0) + p.pnlUsd;
  }
  return map;
}

function buildHeatmap(txns, addr, solPrice) {
  const heuresPnl = Array(24).fill(0).map(() => ({ pnl: 0, count: 0 }));
  const joursPnl = Array(7).fill(0).map(() => ({ pnl: 0, count: 0 }));
  for (const tx of txns) {
    if (tx.type !== 'SWAP' || !tx.timestamp) continue;
    const d = new Date(tx.timestamp * 1000);
    let solIn = 0, solOut = 0;
    for (const nt of tx.nativeTransfers || []) {
      if (nt.toUserAccount === addr) solIn += nt.amount / SOL_DEC;
      if (nt.fromUserAccount === addr) solOut += nt.amount / SOL_DEC;
    }
    const delta = (solIn - solOut) * solPrice;
    const h = d.getHours(), j = (d.getDay() + 6) % 7;
    heuresPnl[h].pnl += delta; heuresPnl[h].count++;
    joursPnl[j].pnl += delta; joursPnl[j].count++;
  }
  const best = (arr) => {
    let idx = null, bestPnl = -Infinity;
    arr.forEach((x, i) => { if (x.count > 0 && x.pnl > bestPnl) { bestPnl = x.pnl; idx = i; } });
    return idx;
  };
  return { heuresPnl, joursPnl, bestHeure: best(heuresPnl), bestJour: best(joursPnl) };
}

// PNL réalisé cumulé (base de coût proportionnelle aux tokens vendus), limité aux mints conservés.
function buildPnlChart(txns, addr, solPrice, keepMints) {
  const book = {}, points = [];
  let cumul = 0;
  const swaps = txns.filter(tx => tx.type === 'SWAP' && tx.timestamp).sort((a, b) => a.timestamp - b.timestamp);
  for (const tx of swaps) {
    let solIn = 0, solOut = 0;
    for (const nt of tx.nativeTransfers || []) {
      if (nt.toUserAccount === addr) solIn += nt.amount / SOL_DEC;
      if (nt.fromUserAccount === addr) solOut += nt.amount / SOL_DEC;
    }
    const wt = (tx.tokenTransfers || []).filter(tt => {
      const amt = parseFloat(tt.tokenAmount ?? 0);
      return amt > 0 && (tt.toUserAccount === addr || tt.fromUserAccount === addr) && keepMints.has(tt.mint);
    });
    const mints = [...new Set(wt.map(tt => tt.mint))];
    const solPerMint = mints.length > 0 ? Math.abs(solIn - solOut) / mints.length : 0;
    let hadSell = false;
    for (const tt of wt) {
      const amt = parseFloat(tt.tokenAmount ?? 0);
      if (!book[tt.mint]) book[tt.mint] = { invested: 0, tokens: 0 };
      const pos = book[tt.mint];
      if (tt.toUserAccount === addr) { pos.invested += solPerMint; pos.tokens += amt; }
      else {
        const pct = pos.tokens > 0 ? Math.min(amt / pos.tokens, 1) : 0;
        const costBasis = pos.invested * pct;
        cumul += (solPerMint - costBasis) * solPrice;
        pos.invested = Math.max(0, pos.invested - costBasis);
        pos.tokens = Math.max(0, pos.tokens - amt);
        hadSell = true;
      }
    }
    if (hadSell) points.push({ ts: tx.timestamp, pnl: cumul });
  }
  if (points.length > 100) {
    const step = Math.ceil(points.length / 100);
    return points.filter((_, i) => i % step === 0 || i === points.length - 1);
  }
  return points;
}

// ─── Verdict de suivi (12 critères, identique à useWalletAnalysis.js) ─────────

export function buildFollowVerdict({ overallRoi, winRate, totalTrades, tradesPerDay, daysActive, rrRatio, avgGainUsd, avgLossUsd, bestTradePnl, totalPnlUsd, closedPositions, avgEntryDelayMin }) {
  if (totalTrades === 0) {
    return { score: 0, recommendation: 'Ne pas suivre', risk: 'Élevé', positives: [],
      warnings: ['Aucune position clôturée — impossible d\'évaluer la rentabilité'], regularityWeeks: null };
  }
  const positives = [], warnings = [];
  let score = 25;

  if (winRate >= 70) { positives.push(`Win rate excellent (${winRate.toFixed(0)}%)`); score += 28; }
  else if (winRate >= 55) { positives.push(`Win rate élevé (${winRate.toFixed(0)}%)`); score += 16; }
  else if (winRate >= 45) { score += 6; }
  else if (winRate > 0 && winRate < 40) { warnings.push(`Win rate faible (${winRate.toFixed(0)}%)`); score -= 18; }
  else if (winRate === 0 && totalTrades > 0) { warnings.push('Aucune victoire détectée'); score -= 25; }

  if (overallRoi >= 100) { positives.push(`ROI exceptionnel (+${overallRoi.toFixed(0)}%)`); score += 20; }
  else if (overallRoi >= 30) { positives.push(`ROI solide (+${overallRoi.toFixed(0)}%)`); score += 12; }
  else if (overallRoi >= 10) { positives.push(`ROI positif (+${overallRoi.toFixed(0)}%)`); score += 6; }
  else if (overallRoi < 0) { warnings.push(`ROI négatif (${overallRoi.toFixed(0)}%)`); score -= 18; }

  if (rrRatio !== null && !isNaN(rrRatio) && rrRatio > 0) {
    if (rrRatio >= 3) { positives.push(`Ratio G/P exceptionnel (${rrRatio.toFixed(1)}x)`); score += 18; }
    else if (rrRatio >= 1.5) { positives.push(`Bon ratio gain/perte (${rrRatio.toFixed(1)}x)`); score += 10; }
    else if (rrRatio >= 1) { score += 4; }
    else if (rrRatio < 0.7) { warnings.push(`Pertes supérieures aux gains (ratio ${rrRatio.toFixed(1)}x)`); score -= 12; }
  }

  if (avgGainUsd > 0 && avgLossUsd > 0 && winRate > 0) {
    const ev = (winRate / 100) * avgGainUsd - ((100 - winRate) / 100) * avgLossUsd;
    if (ev > 50) { positives.push(`Espérance positive (+$${ev.toFixed(0)}/trade en moyenne)`); score += 8; }
    else if (ev > 0) { positives.push('Espérance légèrement positive'); score += 3; }
    else { warnings.push(`Espérance négative ($${ev.toFixed(0)}/trade)`); score -= 8; }
  }

  if (totalTrades >= 30) { positives.push(`${totalTrades} trades — statistiques fiables`); score += 10; }
  else if (totalTrades >= 15) { positives.push(`${totalTrades} trades analysés`); score += 5; }
  else if (totalTrades > 0 && totalTrades < 8) { warnings.push(`Seulement ${totalTrades} trades — peu fiable`); score -= 15; }

  if (daysActive < 7) { warnings.push('Wallet trop récent (< 7 jours)'); score -= 12; }
  else if (daysActive >= 60) { positives.push(`Wallet actif depuis ${daysActive} jours`); score += 6; }
  else if (daysActive >= 14) { score += 3; }

  if (tradesPerDay > BOT_THRESHOLD_TPD) { warnings.push('Activité suspecte — probable bot'); score -= 20; }
  else if (tradesPerDay > 15) { warnings.push(`Trading très fréquent (${tradesPerDay.toFixed(0)} trades/jour)`); score -= 8; }

  if (bestTradePnl != null && totalPnlUsd > 0 && bestTradePnl > 0 && (bestTradePnl / totalPnlUsd) > 0.70) {
    warnings.push(`ROI concentré sur un seul trade (${((bestTradePnl / totalPnlUsd) * 100).toFixed(0)}% du total)`);
    score -= 15;
  }

  const settled = (closedPositions || []).filter(p => p.sortieTs && typeof p.pnlUsd === 'number' && (p.resultat === 'Gagnant' || p.resultat === 'Perdant'));
  if (settled.length >= 3) {
    const cp = [...settled].sort((a, b) => a.sortieTs - b.sortieTs);
    const firstTs = cp[0].sortieTs, buckets = {};
    for (const p of cp) {
      const b = Math.floor((p.sortieTs - firstTs) / (14 * 86400));
      buckets[b] = (buckets[b] || 0) + p.pnlUsd;
    }
    const vals = Object.values(buckets);
    const tot = vals.reduce((s, v) => s + v, 0), maxB = Math.max(...vals);
    if (tot > 0 && vals.length > 1 && maxB / tot > 0.60) {
      warnings.push(`PNL concentré sur 2 semaines (${((maxB / tot) * 100).toFixed(0)}% du total)`);
      score -= 10;
    }
    const cutoff = Date.now() / 1000 - 30 * 86400;
    const recent = settled.filter(p => p.sortieTs >= cutoff);
    if (recent.length >= 3) {
      const recentWr = (recent.filter(p => p.resultat === 'Gagnant').length / recent.length) * 100;
      if (winRate - recentWr > 20) {
        warnings.push(`Dégradation récente : win rate 30j ${recentWr.toFixed(0)}% vs global ${winRate.toFixed(0)}%`);
        score -= 12;
      }
    }
  }

  if (avgEntryDelayMin !== null) {
    if (avgEntryDelayMin < 10) { positives.push(`Sniper précoce — entrée avg ${avgEntryDelayMin < 1 ? '< 1' : avgEntryDelayMin}min après launch`); score += 10; }
    else if (avgEntryDelayMin > 1440) { warnings.push(`Entrée tardive en moyenne (${Math.round(avgEntryDelayMin / 60)}h après launch)`); score -= 5; }
  }

  let regularityWeeks = null;
  if (settled.length >= 3) {
    const nowSec = Date.now() / 1000;
    regularityWeeks = [0, 1, 2, 3].filter(w => {
      const wEnd = nowSec - w * 7 * 86400, wStart = wEnd - 7 * 86400;
      return settled.some(p => p.sortieTs >= wStart && p.sortieTs < wEnd);
    }).length;
    if (regularityWeeks >= 3) { positives.push(`Trading régulier (${regularityWeeks}/4 sem. actives)`); score += 8; }
    else if (regularityWeeks >= 2) { score += 3; }
    else if (regularityWeeks === 0 && settled.length >= 5) {
      warnings.push('Inactif depuis >21j — aucun trade clos dans les 30 derniers jours');
      score -= 8;
    }
  }

  score = Math.max(0, Math.min(100, Math.round(score)));
  let recommendation = score >= 70 ? 'Suivre' : score <= 39 ? 'Ne pas suivre' : 'Observer';
  if (totalTrades < 10 && recommendation === 'Suivre') {
    recommendation = 'Observer';
    warnings.push(`Moins de 10 trades (${totalTrades}) — verdict plafonné à Observer`);
  }
  const risk = recommendation === 'Suivre' ? 'Faible' : recommendation === 'Ne pas suivre' ? 'Élevé' : 'Moyen';
  return { score, recommendation, risk, positives, warnings, regularityWeeks };
}

export function buildConfidence({ txCount, swapCount, daysActive, closedTradesCount }) {
  let score = 30;
  if (txCount >= 300) score += 35; else if (txCount >= 150) score += 25; else if (txCount >= 60) score += 15; else if (txCount >= 20) score += 8;
  if (swapCount >= 40) score += 20; else if (swapCount >= 15) score += 12; else if (swapCount >= 6) score += 6;
  if (daysActive >= 90) score += 15; else if (daysActive >= 30) score += 10; else if (daysActive >= 7) score += 5;
  if (closedTradesCount === 0 && swapCount >= 5) score -= 20;
  score = Math.max(0, Math.min(100, score));
  const level = score >= 75 ? 'Élevée' : score <= 44 ? 'Faible' : 'Moyenne';
  const note = closedTradesCount === 0 && swapCount >= 5
    ? 'Aucune position clôturée — impossible d\'évaluer la rentabilité réelle.'
    : score >= 75 ? 'Bonne profondeur historique : décision plus fiable.'
    : score <= 44 ? 'Échantillon limité : décision à confirmer.'
    : 'Base suffisante pour une décision prudente.';
  return { score, level, note };
}

// ─── Analyse complète ──────────────────────────────────────────────────────

export async function analyzeWallet(addr, apiKey, signal) {
  if (!apiKey) throw new Error('Configurez votre clé Helius.');
  const [solPrice, solBalance, txns] = await Promise.all([
    getSolPrice(signal), getSolBalance(addr, apiKey, signal), getEnhancedTxnsDeep(addr, apiKey, signal),
  ]);

  const book = buildBook(txns, addr);
  const { positions, ignoredDust } = buildPositions(book, solPrice);
  await enrichPositions(positions, signal);
  const s = summarizePositions(positions, solPrice);

  const ts = txns.map(t => t.timestamp).filter(Boolean).sort((a, b) => a - b);
  const now = Date.now() / 1000;
  const daysAct = Math.max(1, (now - (ts[0] || now)) / 86400);
  const swapCount = txns.filter(t => t.type === 'SWAP').length;
  const tradesPerDay = swapCount / daysAct;
  const isBot = tradesPerDay > BOT_THRESHOLD_TPD;

  const verdict = buildFollowVerdict({
    overallRoi: s.overallRoi ?? 0, winRate: s.winRate, totalTrades: s.totalTrades, tradesPerDay,
    daysActive: Math.round(daysAct), rrRatio: s.rrRatio, avgGainUsd: s.avgGainUsd, avgLossUsd: s.avgLossUsd,
    bestTradePnl: s.bestTrade?.pnlUsd ?? null, totalPnlUsd: s.pnlUsd, closedPositions: positions,
    avgEntryDelayMin: s.avgEntryDelayMin,
  });
  const confidence = buildConfidence({ txCount: txns.length, swapCount, daysActive: Math.round(daysAct), closedTradesCount: s.totalTrades });

  let tag;
  if (isBot) tag = { label: 'Bot Probable', color: 'orange' };
  else if (verdict.recommendation === 'Suivre') tag = { label: 'Trader Fiable', color: 'green' };
  else if (verdict.recommendation === 'Ne pas suivre') tag = { label: 'À Éviter', color: 'red' };
  else tag = { label: 'Profil Neutre', color: 'gray' };

  const tokenRows = [...positions].sort((a, b) => {
    if (a.closed !== b.closed) return a.closed ? -1 : 1;
    return a.closed ? b.roi - a.roi : b.investedUsd - a.investedUsd;
  });

  return {
    address: addr, solPrice, solBalance, fetchedAt: Date.now(),
    dateRange: ts.length ? { from: ts[0], to: ts[ts.length - 1] } : null,
    lastActiveTs: ts.length ? ts[ts.length - 1] : null,
    txCount: txns.length, swapCount, daysActive: Math.round(daysAct), tradesPerDay, isBot,
    botScore: isBot ? 94 : Math.min(30, Math.round(tradesPerDay * 2)),
    ignoredDust, ...s,
    positions: [...positions].sort((a, b) => b.entreeTs - a.entreeTs), tokenRows,
    heatmap: buildHeatmap(txns, addr, solPrice),
    pnlChart: buildPnlChart(txns, addr, solPrice, new Set(positions.map(p => p.mint))),
    verdict, confidence, tag,
    tendance: s.overallRoi != null && s.overallRoi > 50 ? 'Anticipe les pumps' : '—',
  };
}
